// ─────────────────────────────────────────────
// KLOTHIQ — Router Configuration
// ─────────────────────────────────────────────

import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { landingPath, permissionForPath } from '@klothiq/domain';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import BrandLoader from '@/components/common/BrandLoader';
import type { ReactNode } from 'react';

// Layouts
import AppLayout from '@/components/layout/AppLayout';

// Auth pages
import LoginPage from '@/pages/auth/LoginPage';
import AccountSetupPage from '@/pages/auth/AccountSetupPage';
import UsersAccessPage from '@/pages/settings/UsersAccessPage';

// Main pages
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ProductListPage from '@/pages/products/ProductListPage';
import ProductFormPage from '@/pages/products/ProductWorkspacePage';
import CatalogConfigurationPage from '@/pages/settings/CatalogConfigurationPage';
import ProductDetailPage from '@/pages/products/ProductDetailPage';
import ImportProductsPage from '@/pages/products/ImportProductsPage';
import InventoryPage from '@/pages/inventory/InventoryPage';
import SupplierListPage from '@/pages/suppliers/SupplierListPage';
import SupplierFormPage from '@/pages/suppliers/SupplierFormPage';
import CustomerListPage from '@/pages/customers/CustomerListPage';
import CustomerFormPage from '@/pages/customers/CustomerFormPage';
import {
  PurchasesRoute as PurchasesListPage,
  PurchaseFormRoute as PurchaseFormPage,
  PurchaseDetailRoute as PurchaseDetailPage,
} from '@/pages/purchases/PurchaseRoutes';
import SalesPerformancePage from '@/pages/sales/SalesPerformancePage';
import SalesListPage from '@/pages/sales/SalesListPage';
import SaleFormPage from '@/pages/sales/SaleFormPage';
import SaleDetailPage from '@/pages/sales/SaleDetailPage';
import PrintTemplatesPage from '@/pages/printing/PrintTemplatesPage';
import DeliveriesPage from '@/pages/delivery/DeliveriesPage';
import DeliveryDetailPage from '@/pages/delivery/DeliveryDetailPage';
import CashAccountsPage from '@/pages/cash/CashAccountsPage';
import PaymentFormPage from '@/pages/payments/PaymentFormPage';
import AuditHistoryPage from '@/pages/audit/AuditHistoryPage';
import RecycleBinPage from '@/pages/recycle/RecycleBinPage';
import DataImportPage from '@/pages/settings/DataImportPage';
import MasterConfigPage from '@/pages/settings/MasterConfigPage';
import ReportsHubPage from '@/pages/reports/ReportsHubPage';
import ReportDetailPage from '@/pages/reports/ReportDetailPage';

// Accounts Module (FEAT-ACCOUNTS-001)
import AccountsLayout from '@/pages/accounts/AccountsLayout';
import AccountsOverviewTab from '@/pages/accounts/tabs/AccountsOverviewTab';
import CashbookTab from '@/pages/accounts/tabs/CashbookTab';
import CapitalPartnersTab from '@/pages/accounts/tabs/CapitalPartnersTab';
import ExpensesTab from '@/pages/accounts/tabs/ExpensesTab';
import CashbookImportPage from '@/pages/accounts/CashbookImportPage';

// Plans and Tasks Module (FEAT-TASK-PLANNING-001)
import PlansListPage from '@/pages/plans/PlansListPage';
import PlanDetailPage from '@/pages/plans/PlanDetailPage';
import MyTasksPage from '@/pages/tasks/MyTasksPage';

// FEAT-LOADER-001 §9: a boot that never resolves within 5s almost
// certainly means slow auth/profile-fetch latency upstream, not a loader
// problem — console-only signal, no user-facing alarm.
function useSlowBootWarning(isLoading: boolean) {
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => {
      console.warn('[BrandLoader] boot loading exceeded 5s — check auth/profile fetch latency');
    }, 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);
}

/**
 * Route guard — redirects to login if not authenticated.
 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, access, can, signOut } = useAuth();
  const location = useLocation();
  useSlowBootWarning(isLoading);

  if (isLoading) {
    return <BrandLoader variant="boot" active immediate message="Loading KLOTHIQ..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!access?.ready) return <AccountSetupPage />;
  const permission = permissionForPath(location.pathname);
  if (permission && !can(permission)) {
    const destination = landingPath(access);
    if (location.pathname === '/' && destination !== '/' && destination !== '/access-unavailable')
      return <Navigate to={destination} replace />;
    return (
      <main className="flex min-h-screen items-center justify-center bg-background p-6">
        <section className="max-w-md space-y-4 rounded-2xl border border-border bg-card p-6">
          <h1 className="text-xl font-bold">Screen access unavailable</h1>
          <p className="text-sm text-muted-foreground">
            Your Super Admin can configure the screens available to your login.
          </p>
          {destination !== '/access-unavailable' && (
            <a href={destination} className="block text-sm font-semibold text-primary">
              Open my workspace
            </a>
          )}
          <button
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
            onClick={() => signOut()}
          >
            Sign out
          </button>
        </section>
      </main>
    );
  }

  return <>{children}</>;
}

/**
 * Redirect authenticated users away from login.
 */
function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  useSlowBootWarning(isLoading);

  if (isLoading) {
    return <BrandLoader variant="boot" active immediate message="Loading KLOTHIQ..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: 'deliveries', element: <DeliveriesPage /> },
      { path: 'deliveries/:saleId', element: <DeliveryDetailPage /> },
      { index: true, element: <DashboardPage /> },
      // Products
      { path: 'products', element: <ProductListPage /> },
      { path: 'products/import', element: <ImportProductsPage /> },
      { path: 'products/new', element: <ProductFormPage /> },
      { path: 'products/:id', element: <ProductDetailPage /> },
      { path: 'products/:id/edit', element: <ProductFormPage /> },
      // Inventory
      { path: 'inventory', element: <InventoryPage /> },
      // Suppliers
      { path: 'suppliers', element: <SupplierListPage /> },
      { path: 'suppliers/new', element: <SupplierFormPage /> },
      { path: 'suppliers/:id/edit', element: <SupplierFormPage /> },
      // Customers
      { path: 'customers', element: <CustomerListPage /> },
      { path: 'customers/new', element: <CustomerFormPage /> },
      { path: 'customers/:id/edit', element: <CustomerFormPage /> },
      // Purchases
      { path: 'purchases', element: <PurchasesListPage /> },
      { path: 'purchases/new', element: <PurchaseFormPage /> },
      { path: 'purchases/:id', element: <PurchaseDetailPage /> },
      { path: 'purchases/:id/edit', element: <PurchaseFormPage /> },
      // Sales
      { path: 'sales', element: <SalesListPage /> },
      { path: 'sales/new', element: <SaleFormPage /> },
      { path: 'sales/:id', element: <SaleDetailPage /> },
      { path: 'sales/:id/edit', element: <SaleFormPage /> },
      { path: 'print-templates', element: <PrintTemplatesPage /> },
      // Accounts Module (FEAT-ACCOUNTS-001)
      {
        path: 'accounts',
        element: <AccountsLayout />,
        children: [
          { index: true, element: <AccountsOverviewTab /> },
          { path: 'cashbook', element: <CashbookTab /> },
          { path: 'capital', element: <CapitalPartnersTab /> },
          { path: 'expenses', element: <ExpensesTab /> },
          { path: 'balances', element: <CashAccountsPage embedded /> },
        ],
      },
      { path: 'accounts/cashbook/import', element: <CashbookImportPage /> },
      // Cash & Payments legacy redirect (FR-017)
      { path: 'cash', element: <Navigate to="/accounts/balances" replace /> },
      { path: 'payments/new', element: <PaymentFormPage /> },
      // Plans & Tasks (FEAT-TASK-PLANNING-001)
      { path: 'plans', element: <PlansListPage /> },
      { path: 'plans/:planId', element: <PlanDetailPage /> },
      { path: 'my-tasks', element: <MyTasksPage /> },
      { path: 'my-sales', element: <SalesPerformancePage /> },
      { path: 'team-sales', element: <SalesPerformancePage team /> },
      // Audit & Recycle Bin
      { path: 'audit', element: <AuditHistoryPage /> },
      { path: 'recycle-bin', element: <RecycleBinPage /> },
      { path: 'master-config', element: <MasterConfigPage /> },
      { path: 'master-config/catalog', element: <CatalogConfigurationPage /> },
      { path: 'settings/import', element: <DataImportPage /> },
      { path: 'settings/users', element: <UsersAccessPage /> },
      { path: 'my-account', element: <AccountSetupPage /> },
      // Reports Module
      { path: 'reports', element: <ReportsHubPage /> },
      { path: 'reports/:category/:type', element: <ReportDetailPage /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
